import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { AiOutlineBars } from "react-icons/ai";
import { ArrowLeft } from "lucide-react";
import toast from "react-hot-toast";

import {
  navigateReviewSession,
  getReviewSession,
  getReviewPatientReadings,
  saveReviewPatientActions,
  setReviewSessionState,
  getSessionConflict,
  getApiErrorMessage,
  SESSION_CONFLICT,
} from "../../../../services/case-manager/readingReviewSession";
import { getPatientGeneralDetails, getPatientMLSummary } from "../../../../services/patient/details";
import { setPatientLocationId } from "../../../../utils/helper";
import Spinner from "../../../../components/spinner";
import DailiesPatientInfo from "../components/DailiesPatientInfo";
import DailiesRightPanel from "../components/DailiesRightPanel";
import SessionReadingsPanel, { STATUS_LABEL } from "./SessionReadingsPanel";
import SessionListPanel from "./SessionListPanel";
import {
  LeaveSessionDialog,
  SessionEndedDialog,
  MarkStatusDialog,
  SessionPausedDialog,
  UnsavedChangesDialog,
} from "./SessionDialogs";
import useSessionActivity from "./useSessionActivity";
import {
  announceTimeSaved,
  clearOpenPatient,
  onTimeSavedElsewhere,
  setOpenPatient,
  signalReviewTimer,
} from "./openPatient";
import { saveResumePoint } from "./resumePoint";
import styles from "./ReadingReviewSession.module.css";

const PAUSE_MESSAGES = {
  manual: "Your review session has been paused.",
  inactive: "Your review session was paused after a period of inactivity.",
  left_tab: "Your review session was paused because you left this tab.",
  ended: "Your review session was paused - it may have been opened elsewhere.",
};

// Status cycle for a reading's pill: Reviewed → Escalated → Not Reviewed →
// Reviewed → … from whatever it's saved as - any reading can be changed
// again, including on a patient already completed (an unmarked reading
// makes that patient pending again, and progress goes back down).
const STATUS_CYCLE = ["reviewed", "escalated", "not_reviewed"];
const nextStagedStatus = (reading, staged) => {
  const current = staged || reading.status;
  const next = STATUS_CYCLE[(STATUS_CYCLE.indexOf(current) + 1) % STATUS_CYCLE.length];
  // Back at the saved status means nothing left to save for this reading.
  return next === reading.status ? undefined : next;
};

// Reading ids per target status, for API #10.
const toActionLists = (pairs) => ({
  reviewed: pairs.filter(([, s]) => s === "reviewed").map(([id]) => Number(id)),
  escalated: pairs.filter(([, s]) => s === "escalated").map(([id]) => Number(id)),
  notReviewed: pairs.filter(([, s]) => s === "not_reviewed").map(([id]) => Number(id)),
});

const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;

// SCRUM-962: the admin who ended the session, if the API names them. The v2
// contract records it on the terminate event but doesn't document a field
// on the 409 / Session object yet, so this reads the likely ones and falls
// back to null ("an administrator").
const adminNameFrom = (payload) => {
  const by = payload?.terminated_by || payload?.ended_by || payload?.abandoned_by;
  return (typeof by === "string" ? by : by?.name) || null;
};

const ENDED_CHECK_MS = 30 * 1000;

// One toast for the single Save (readings + notes). ok: true = saved,
// false = failed, null = that part had nothing to save.
const showSaveResult = (readingsOk, notesOk) => {
  const warn = (message) => toast(message, { icon: "⚠️", duration: 6000 });
  if (readingsOk !== null && notesOk !== null) {
    if (readingsOk && notesOk) toast.success("Readings and notes saved successfully.");
    else if (readingsOk) warn("Readings saved successfully, but your notes could not be saved. Please try saving your notes again.");
    else if (notesOk) warn("Notes saved successfully, but the readings could not be saved. Please try saving the readings again.");
    else toast.error("Readings and notes could not be saved. Please try again.");
  } else if (readingsOk !== null) {
    if (readingsOk) toast.success("Readings saved successfully.");
    else toast.error("Readings could not be saved. Please try again.");
  } else if (notesOk !== null) {
    if (notesOk) toast.success("Notes saved successfully.");
    else toast.error("Notes could not be saved. Please try again.");
  }
};

// SCRUM-851 (+ 850, 852): the session workspace. Works one patient at a time
// in the order the server returns (API #7), saves statuses (API #10), and
// tracks progress from the Session object (it drops again when a completed
// patient gets a reading set back to Not Reviewed).
const SessionWorkspace = ({ accId, initialSession, restore, timerValues = {}, onExit, onCompleted }) => {
  const navigate = useNavigate();
  const [, setSearchParams] = useSearchParams();
  const [session, setSession] = useState(initialSession);
  const [patientEntry, setPatientEntry] = useState(null);
  // `restore` (after Patient Details): the view to come back to.
  const [readingType, setReadingType] = useState(restore?.readingType || "");
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [statusChanges, setStatusChanges] = useState({});
  const [listOpen, setListOpen] = useState(!!restore?.listOpen);
  // Keeps the restored reading-type filter through the first navigation.
  const restoringRef = useRef(!!restore);
  const [listRefresh, setListRefresh] = useState(0);
  const [busy, setBusy] = useState(false);
  const [paused, setPaused] = useState(null); // {message} while paused
  const [resuming, setResuming] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [markRequest, setMarkRequest] = useState(null);
  const [leaveOpen, setLeaveOpen] = useState(false);
  const [leaving, setLeaving] = useState(false);
  // SCRUM-962: set (to the admin's name, or null if unknown) once an admin
  // has ended this session - shows "Your Session Has Been Ended".
  const [endedBy, setEndedBy] = useState(undefined);
  const sessionEnded = endedBy !== undefined;

  const sessionId = session.id;
  const patientId = patientEntry?.patient?.id || null;
  const hasStaged = Object.keys(statusChanges).length > 0;
  // The Notes card (single-Save mode): its controller, and whether a note
  // has been typed - unsaved work alongside staged statuses.
  const notesRef = useRef(null);
  const [noteTyped, setNoteTyped] = useState(false);
  const hasUnsaved = hasStaged || noteTyped;

  // ── Conflicts (409) ───────────────────────────────────────────────────────
  const activityRef = useRef(null);
  const handleConflict = useCallback(
    (conflict) => {
      if (!conflict) return false;
      if (conflict.code === SESSION_CONFLICT.PAUSED) {
        activityRef.current?.markPaused();
        setPaused({ message: PAUSE_MESSAGES.ended });
      } else if (conflict.code === SESSION_CONFLICT.CLOSED) {
        // SCRUM-962: an admin ended this session ("Your Session Has Been
        // Ended"); Ok goes back to My Tasks' selection screen.
        activityRef.current?.markPaused();
        setEndedBy(adminNameFrom(conflict));
      } else {
        // session_locked / session_closed: back to the selection screen.
        toast.error(conflict.detail || "This review session is no longer available.");
        onExit();
      }
      return true;
    },
    [onExit]
  );
  const handleError = (error, fallback) => {
    if (handleConflict(getSessionConflict(error))) return;
    toast.error(getApiErrorMessage(error, fallback));
  };

  const activity = useSessionActivity({
    accId,
    session,
    enabled: session.status === "active" && session.is_mine && !paused && !sessionEnded,
    onPaused: (reason, idleSeconds) =>
      setPaused({ message: PAUSE_MESSAGES[reason] || PAUSE_MESSAGES.manual, idleSeconds }),
    onConflict: handleConflict,
  });
  activityRef.current = activity;

  // SCRUM-962: an admin can end this session from Admin › Active Review
  // Sessions. Check its status every 30s so the reviewer is told even if
  // they aren't clicking anything (otherwise they'd only find out on their
  // next action, via 409 session_closed).
  const watching = session.status === "active" && session.is_mine && !paused && !sessionEnded;
  useEffect(() => {
    if (!watching) return undefined;
    const t = setInterval(async () => {
      try {
        const fresh = await getReviewSession(accId, sessionId);
        if (fresh.status === "abandoned") {
          activityRef.current?.markPaused();
          setEndedBy(adminNameFrom(fresh));
        }
      } catch (error) {
        handleConflict(getSessionConflict(error));
      }
    }, ENDED_CHECK_MS);
    return () => clearInterval(t);
  }, [watching, accId, sessionId, handleConflict]);

  // ── Patient navigation (API #7) ───────────────────────────────────────────
  const applyNavigation = useCallback(
    (res) => {
      setSession(res.session);
      if (res.left_patient && res.left_patient.completed === false && res.left_patient.unmarked_readings > 0) {
        toast(
          `${plural(res.left_patient.unmarked_readings, "reading")} on the previous patient still need${
            res.left_patient.unmarked_readings === 1 ? "s" : ""
          } a status - that patient stays in the session.`,
          { icon: "ℹ️" }
        );
      }
      if (res.session.status === "completed" || !res.patient) {
        activityRef.current?.markPaused();
        onCompleted(res.session);
        return;
      }
      setPatientEntry(res.patient);
      if (!restoringRef.current) setReadingType("");
      restoringRef.current = false;
      setSelectedIds(new Set());
      setStatusChanges({});
      setListRefresh((n) => n + 1);
      // The timer, notes and monitoring panels read the current patient from
      // here, same as the classic Readings Review.
      sessionStorage.setItem("_pt_id", res.patient.patient.id);
      // Starts (or moves) the My Tasks RPM timer to this patient.
      setOpenPatient(res.patient.patient.id, res.session.id);
      if (res.patient.location?.id) setPatientLocationId(res.patient.location.id);
    },
    [onCompleted]
  );

  const goTo = useCallback(
    async (to, completeCurrent = false) => {
      setBusy(true);
      try {
        applyNavigation(await navigateReviewSession(accId, sessionId, to, completeCurrent));
      } catch (error) {
        handleError(error, "Could not open that patient.");
      } finally {
        setBusy(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [accId, sessionId, applyNavigation]
  );

  // Leaving the workspace (Back to Selection, All Done, another tab/route)
  // stops the RPM timer, which saves the time counted on that patient.
  useEffect(() => clearOpenPatient, []);

  // SCRUM-850: the RPM timer stops while the session is paused (Pause
  // Session, idle, tab left) or ended - saving the time recorded so far, and
  // without the idle minutes after an idle pause - and carries on from the
  // same count on Resume Session.
  const timerHeld = !!paused || sessionEnded;
  useEffect(() => {
    signalReviewTimer(timerHeld ? "pause" : "resume", { rewindSeconds: paused?.idleSeconds || 0 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timerHeld]);

  // SCRUM-850: a note saved here (it dispatches "trigger-save") records this
  // patient's time. A duplicate tab paused on the same patient then matches
  // this one - timer back to 00:00 and stopped, no Session Paused popup -
  // and carries on quietly when the reviewer comes back to it.
  useEffect(() => {
    if (!patientId) return undefined;
    const announce = () => announceTimeSaved(sessionId, patientId);
    window.addEventListener("trigger-save", announce);
    return () => window.removeEventListener("trigger-save", announce);
  }, [sessionId, patientId]);

  useEffect(() => {
    if (!paused || paused.quiet || !patientId) return undefined;
    return onTimeSavedElsewhere((saved) => {
      if (saved.sessionId !== String(sessionId) || saved.patientId !== String(patientId)) return;
      signalReviewTimer("reset");
      setPaused((p) => p && { ...p, quiet: true });
    });
  }, [paused, sessionId, patientId]);

  // The wait lets the other tab's own pause (it was just left) land first.
  const quietResume = !!paused?.quiet;
  useEffect(() => {
    if (!quietResume) return undefined;
    let timer;
    const resumeWhenShown = () => {
      clearTimeout(timer);
      if (document.visibilityState !== "visible") return;
      timer = setTimeout(async () => {
        try {
          await continueSession();
        } catch (error) {
          // Couldn't carry on: show the popup after all.
          setPaused((p) => p && { ...p, quiet: false });
          handleConflict(getSessionConflict(error));
        }
      }, 1500);
    };
    resumeWhenShown();
    document.addEventListener("visibilitychange", resumeWhenShown);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("visibilitychange", resumeWhenShown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quietResume]);

  // First load marks the current patient as opened (or, coming back from
  // Patient Details, the patient that was open then).
  useEffect(() => {
    goTo(restore?.patientId || initialSession.current_patient_id || "next");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Data ──────────────────────────────────────────────────────────────────
  const readingsQuery = useQuery(
    ["reading-review", "readings", sessionId, patientId, readingType],
    () => getReviewPatientReadings(accId, sessionId, patientId, readingType || undefined),
    {
      enabled: !!patientId,
      staleTime: 0,
      keepPreviousData: true,
      onError: (error) => handleError(error, "Could not load readings."),
    }
  );
  const readings = useMemo(
    () => (readingsQuery.data?.groups || []).flatMap((g) => g.readings),
    [readingsQuery.data]
  );

  // The All Data graph (Charts) reads the program from the URL (?RPM=1), as
  // the classic Readings Review sets it - same patient-details query (and
  // cache) the patient card uses.
  const { data: patientDetails } = useQuery(
    ["patient-details", patientId],
    () => getPatientGeneralDetails(patientId),
    { enabled: !!patientId, staleTime: 5 * 60 * 1000 }
  );
  useEffect(() => {
    if (!patientDetails) return;
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        params.set("RPM", Number(patientDetails.rpm ?? true));
        return params;
      },
      { replace: true }
    );
  }, [patientDetails, setSearchParams]);

  const { data: mlSummary, isLoading: summaryLoading } = useQuery(
    ["patient-ml-summary", patientId],
    () => getPatientMLSummary(patientId),
    { enabled: !!patientId, staleTime: 5 * 60 * 1000, retry: 1 }
  );

  const refreshProgress = async () => {
    try {
      // Save never moves on (SCRUM-766) - Next Patient finishes the session
      // once every patient is done.
      setSession(await getReviewSession(accId, sessionId));
    } catch (error) {
      handleConflict(getSessionConflict(error));
    }
  };

  // ── Saving (API #10) ──────────────────────────────────────────────────────
  // `silent`: the single Save reports readings + notes in one toast itself.
  const save = async ({ reviewed, escalated, notReviewed }, { silent = false } = {}) => {
    const res = await saveReviewPatientActions(accId, sessionId, patientId, { reviewed, escalated, notReviewed });
    const skipped = res.skipped?.length || 0;
    const errors = res.errors?.length || 0;
    if (!silent) {
      toast.success(
        `Saved ${plural(res.updated?.length || 0, "reading")}${skipped ? ` · ${skipped} skipped` : ""}${
          errors ? ` · ${errors} not saved` : ""
        }`
      );
      if (errors) (res.errors || []).slice(0, 3).forEach((e) => toast.error(e.reason));
    }
    setSession((s) => ({ ...s, completed_patients: res.completed_patients ?? s.completed_patients }));
    refreshProgress();
    readingsQuery.refetch();
    setListRefresh((n) => n + 1);
    return res;
  };

  // SCRUM-851 #12 / SCRUM-766: the bottom Save is the only Save. It saves
  // the reading status changes (API #10) and the Notes card's note + time
  // (via its controller) in one click, and keeps the reviewer on this
  // patient. Resolves true when everything that needed saving was saved.
  const saveAll = async () => {
    const lists = toActionLists(Object.entries(statusChanges));
    const hasStatuses = lists.reviewed.length || lists.escalated.length || lists.notReviewed.length;
    const notes = notesRef.current;
    const withNote = !!notes?.hasNote();
    if (!hasStatuses && !withNote) return true;
    setBusy(true);
    // Each part is saved independently (one failing doesn't stop the
    // other); null = nothing to save for that part.
    let readingsOk = null;
    let notesOk = null;
    try {
      if (hasStatuses) {
        try {
          await save(lists, { silent: true });
          setStatusChanges({});
          readingsOk = true;
        } catch (error) {
          readingsOk = false;
          // Session paused / closed / taken: same handling as anywhere else.
          handleConflict(getSessionConflict(error));
        }
      }
      if (withNote) notesOk = await notes.submit({ silent: true });
    } finally {
      setBusy(false);
    }
    showSaveResult(readingsOk, notesOk);
    return readingsOk !== false && notesOk !== false;
  };

  // ── Bulk "Mark Selected …" with mixed-status confirmation ────────────────
  const selectedReadings = readings.filter((r) => selectedIds.has(r.id));

  // The clicked action is the source of truth: every selected reading not
  // already at the target status is changed to it.
  const applyMark = async (target) => {
    const changing = selectedReadings.filter((r) => r.status !== target);
    if (!changing.length) {
      toast(`The selected readings are already ${STATUS_LABEL[target]}.`);
      setMarkRequest(null);
      return;
    }
    setBusy(true);
    try {
      await save(toActionLists(changing.map((r) => [r.id, target])));
      setSelectedIds(new Set());
      // The bulk action is the source of truth for the readings it covered.
      setStatusChanges((prev) => {
        const next = { ...prev };
        selectedReadings.forEach((r) => delete next[r.id]);
        return next;
      });
      setMarkRequest(null);
    } catch (error) {
      handleError(error, "Could not update the selected readings.");
    } finally {
      setBusy(false);
    }
  };

  // Mixed statuses (SCRUM-851): selected readings already marked something
  // other than the target are listed in a confirmation first. Plain "not
  // reviewed" readings being marked are the normal case and don't ask.
  const requestMark = (target) => {
    const counts = {};
    selectedReadings.forEach((r) => {
      if (r.status !== target && r.status !== "not_reviewed") counts[r.status] = (counts[r.status] || 0) + 1;
    });
    if (Object.keys(counts).length === 0) {
      applyMark(target);
      return;
    }
    setMarkRequest({ target, total: selectedReadings.length, counts });
  };

  // ── Unsaved-changes guard ─────────────────────────────────────────────────
  const guard = (action) => {
    if (hasUnsaved) setPendingAction(() => action);
    else action();
  };

  const toggleStatus = (reading) => {
    setStatusChanges((prev) => {
      const next = { ...prev };
      const value = nextStagedStatus(reading, prev[reading.id]);
      if (value) next[reading.id] = value;
      else delete next[reading.id];
      return next;
    });
  };

  // ── Pause / resume (SCRUM-850) ───────────────────────────────────────────
  const pauseSession = async () => {
    activity.markPaused();
    setPaused({ message: PAUSE_MESSAGES.manual });
    try {
      setSession(await setReviewSessionState(accId, sessionId, "pause", "manual"));
    } catch {
      // Pausing is safe to repeat; a failure here leaves it to the idle timeout.
    }
  };

  // Carries on with the session on the patient it's on now - another
  // browser tab may have moved it to the next patient while this one was
  // paused.
  async function continueSession() {
    const fresh = await setReviewSessionState(accId, sessionId, "continue");
    setSession(fresh);
    setPaused(null);
    if (fresh.current_patient_id && String(fresh.current_patient_id) !== String(patientId)) {
      goTo(fresh.current_patient_id);
    } else {
      readingsQuery.refetch();
      setListRefresh((n) => n + 1);
    }
  }

  const resumeSession = async () => {
    setResuming(true);
    try {
      await continueSession();
    } catch (error) {
      handleError(error, "Could not resume the session.");
    } finally {
      setResuming(false);
    }
  };

  // Back arrow → "Leave Review Session?" → Go Back pauses the session (so
  // anyone can continue it later) and returns to account / care manager
  // selection. Unsaved statuses are dealt with first (guard).
  const leaveToSelection = async () => {
    setLeaving(true);
    activity.markPaused();
    signalReviewTimer("pause");
    try {
      await setReviewSessionState(accId, sessionId, "pause", "manual");
    } catch {
      // Safe to repeat; a failure leaves it to the idle timeout.
    }
    setLeaving(false);
    setLeaveOpen(false);
    onExit();
  };

  const openPatientDetails = (patient) => {
    guard(() => {
      // Patient Details' Back returns to My Tasks; ReadingReviewSession
      // picks the session back up from this.
      saveResumePoint({ accId, sessionId, patientId, readingType, listOpen });
      sessionStorage.setItem("_pt_id", patientId);
      sessionStorage.setItem("_back_page", "my-tasks");
      sessionStorage.setItem("my-tasks/active-tab", "dailies");
      navigate(
        `/patient/details/?patient_id=${patientId}&CCM=${Number(!!patient?.ccm)}&RPM=${Number(patient?.rpm ?? true)}`
      );
    });
  };

  // ── Render ────────────────────────────────────────────────────────────────
  const progress = Math.min(Math.max(session.progress_percent || 0, 0), 100);
  // next_patient_id is the next patient still to review (null when none is
  // left besides this one) - only one patient, or the last one left.
  // Once every patient is done, Next Patient stays enabled - it finishes the
  // session and shows "All Done" (Save itself never moves on, SCRUM-766).
  const allDone = session.total_patients > 0 && session.completed_patients >= session.total_patients;
  const noOtherPatients =
    !!patientId &&
    !allDone &&
    (!session.next_patient_id || String(session.next_patient_id) === String(patientId));

  return (
    <div className={styles.workspace}>
      <div className={styles.workspaceMain}>
        <div className={styles.sessionHeader}>
          <span className={styles.sessionHeaderLeft}>
            <button
              type="button"
              className={styles.backBtn}
              onClick={() => guard(() => setLeaveOpen(true))}
              disabled={busy}
              aria-label="Back to account and care manager selection"
              title="Back to selection"
            >
              <ArrowLeft size={20} strokeWidth={2.5} />
            </button>
            <span className={styles.sessionHeaderLabel}>
              Care Manager : <strong>{session.case_manager?.name}</strong>
            </span>
          </span>
          <div className={styles.progressBlock}>
            <div className={styles.progressText}>
              <span>{progress} % completed</span>
              <span>
                {session.completed_patients}/{session.total_patients} Patients
              </span>
            </div>
            <div
              className={styles.progressTrack}
              role="progressbar"
              aria-valuenow={progress}
              aria-valuemin={0}
              aria-valuemax={100}
            >
              <div className={styles.progressFill} style={{ width: `${progress}%` }} />
            </div>
          </div>
          <div className={styles.sessionHeaderRight}>
            <button type="button" className={styles.pauseBtn} onClick={() => guard(pauseSession)} disabled={busy}>
              Pause Session
            </button>
          </div>
        </div>

        {!patientId ? (
          <div style={{ display: "flex", justifyContent: "center", padding: 60 }}>
            <Spinner />
          </div>
        ) : (
          <div className={styles.workspaceBody}>
            <div className={styles.leftColumn}>
              <div className={styles.leftColumnInner}>
              <DailiesPatientInfo patientId={patientId} variant="session" onNameClick={openPatientDetails} />
              <SessionReadingsPanel
                patientId={patientId}
                data={readingsQuery.data}
                isLoading={readingsQuery.isLoading}
                isError={readingsQuery.isError}
                readingType={readingType}
                onReadingTypeChange={(value) => {
                  setReadingType(value);
                  setSelectedIds(new Set());
                }}
                selectedIds={selectedIds}
                onSelectionChange={setSelectedIds}
                statusChanges={statusChanges}
                onToggleStatus={toggleStatus}
              />
              </div>
            </div>
            <div className={styles.rightColumn}>
              {/* Same props as the classic Readings Review, so Notes behaves
                  identically (live monitoring time from the My Tasks tracker). */}
              <DailiesRightPanel
                patientId={patientId}
                summary={mlSummary || patientDetails?.summary}
                summaryLoading={summaryLoading}
                noteType="rpm_note"
                caseManager={true}
                noteDropdownLabel="Pre-completed note"
                rpmTimeFormat="HH:MM"
                showAiInsights={true}
                defaultTimer={timerValues}
                resetTimer={() => {}}
                hideNotesSave
                notesControllerRef={notesRef}
                onNoteChange={setNoteTyped}
              />
            </div>
          </div>
        )}

        <div className={styles.footer}>
          {selectedIds.size > 0 ? (
            <div className={styles.bulkBar}>
              <button
                type="button"
                className={`${styles.bulkBtn} ${styles.bulkNotReviewed}`}
                onClick={() => requestMark("not_reviewed")}
                disabled={busy}
              >
                Mark Selected &ldquo;Not Reviewed&rdquo;
              </button>
              <button
                type="button"
                className={`${styles.bulkBtn} ${styles.bulkReviewed}`}
                onClick={() => requestMark("reviewed")}
                disabled={busy}
              >
                Mark Selected &ldquo;Reviewed&rdquo;
              </button>
              <button
                type="button"
                className={`${styles.bulkBtn} ${styles.bulkEscalated}`}
                onClick={() => requestMark("escalated")}
                disabled={busy}
              >
                Mark Selected &ldquo;Escalated&rdquo;
              </button>
              <span className={styles.bulkCount}>{selectedIds.size} selected</span>
            </div>
          ) : (
            <>
              <button
                type="button"
                className={styles.footerBtn}
                onClick={() => guard(() => goTo(session.previous_patient_id))}
                disabled={busy || !session.previous_patient_id}
              >
                Previous Patient
              </button>
              <div className={styles.footerRight}>
                <button type="button" className={styles.footerBtnOutline} onClick={saveAll} disabled={busy || !hasUnsaved}>
                  Save
                </button>
                {/* Wrapper carries the hover text: a disabled button gets
                    no mouse events in some browsers. */}
                <span title={noOtherPatients ? "No other patients to review" : undefined}>
                  <button
                    type="button"
                    className={styles.footerBtnPrimary}
                    onClick={() => guard(() => goTo("next", true))}
                    disabled={busy || !patientId || noOtherPatients}
                    style={noOtherPatients ? { pointerEvents: "none" } : undefined}
                  >
                    Next Patient
                  </button>
                </span>
              </div>
            </>
          )}
        </div>
      </div>

      {listOpen ? (
        <SessionListPanel
          accId={accId}
          sessionId={sessionId}
          currentPatientId={patientId}
          refreshKey={listRefresh}
          onSelectPatient={(id) => guard(() => goTo(id))}
          onClose={() => setListOpen(false)}
        />
      ) : (
        <button type="button" className={styles.sessionListTab} onClick={() => setListOpen(true)}>
          <AiOutlineBars size={16} aria-hidden="true" />
          <span className={styles.sessionListTabText}>Session List</span>
        </button>
      )}

      <UnsavedChangesDialog
        open={!!pendingAction}
        busy={busy}
        onCancel={() => setPendingAction(null)}
        onDiscard={() => {
          const action = pendingAction;
          setPendingAction(null);
          setStatusChanges({});
          action?.();
        }}
        onSave={async () => {
          const action = pendingAction;
          if (await saveAll()) {
            setPendingAction(null);
            action?.();
          }
        }}
      />

      <MarkStatusDialog
        request={markRequest}
        busy={busy}
        onCancel={() => setMarkRequest(null)}
        onConfirm={() => applyMark(markRequest.target)}
      />

      <SessionEndedDialog
        open={sessionEnded}
        adminName={endedBy}
        onOk={() => {
          setEndedBy(undefined);
          onExit();
        }}
      />

      <LeaveSessionDialog
        open={leaveOpen}
        busy={leaving}
        onGoBack={leaveToSelection}
        onStay={() => setLeaveOpen(false)}
      />

      <SessionPausedDialog
        open={!!paused && !paused.quiet}
        message={paused?.message}
        busy={resuming}
        onBackToSelection={onExit}
        onResume={resumeSession}
      />
    </div>
  );
};

export default SessionWorkspace;
